import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ActiveBrandEnvironment } from 'js/src/brand-environments/types';
import type { BrandEnvironment } from 'js/src/client';

export interface BrandEnvironmentsState {
    /** The environment the agent builds in, or null when none applies. */
    active: ActiveBrandEnvironment | null;
    /** Every environment the user can see, as listed on the brand environments page. Null until loaded. */
    environments: BrandEnvironment[] | null;
    /** The environments a new video can be built in from this workspace. Null until loaded. */
    available: BrandEnvironment[] | null;
    /** The environment the open video was made in. Once a video exists, this is the one that applies. */
    videoEnvironmentId: number | null;
}

const initialState: BrandEnvironmentsState = {
    active: null,
    environments: null,
    available: null,
    videoEnvironmentId: null,
};

const brandEnvironmentsSlice = createSlice({
    name: 'brandEnvironments',
    initialState,
    reducers: {
        setActiveBrandEnvironment: (
            state,
            action: PayloadAction<ActiveBrandEnvironment | null>,
        ) => {
            state.active = action.payload;
        },
        setBrandEnvironments: (state, action: PayloadAction<BrandEnvironment[]>) => {
            state.environments = action.payload;
        },
        upsertBrandEnvironment: (state, action: PayloadAction<BrandEnvironment>) => {
            const environments = state.environments ?? [];
            const index = environments.findIndex(
                (environment) => environment.id === action.payload.id,
            );
            state.environments =
                index === -1
                    ? [...environments, action.payload]
                    : environments.map((environment, i) =>
                          i === index ? action.payload : environment,
                      );
        },
        setAvailableBrandEnvironments: (state, action: PayloadAction<BrandEnvironment[]>) => {
            state.available = action.payload;
        },
        setVideoBrandEnvironmentId: (state, action: PayloadAction<number | null>) => {
            state.videoEnvironmentId = action.payload;
        },
        removeBrandEnvironment: (state, action: PayloadAction<number>) => {
            state.environments = (state.environments ?? []).filter(
                (environment) => environment.id !== action.payload,
            );
        },
    },
});

export const {
    setActiveBrandEnvironment,
    setBrandEnvironments,
    upsertBrandEnvironment,
    removeBrandEnvironment,
    setAvailableBrandEnvironments,
    setVideoBrandEnvironmentId,
} = brandEnvironmentsSlice.actions;

export default brandEnvironmentsSlice.reducer;
